import { isNullOrEmpty } from '@vestigas/utils';

export type buildSearchParamsType = {
  (
    key: string,
    value: string | number | boolean | undefined,
    searchParams: URLSearchParams,
    toSearchString?: true,
  ): string;
  (
    key: string,
    value: string | number | boolean | undefined,
    searchParams: URLSearchParams,
    toSearchString?: false,
  ): URLSearchParams;
};

/**
 * Builds a new URLSearchParams object by adding or updating a key-value pair.
 * @param key - The key to add or update. The key is removed if the value is an empty string.
 * @param value - The value to set for the key.
 * @param searchParams - The original URLSearchParams object.
 * @param toSearchString - Optional. If true, returns the search params as a string with a leading "?".
 * @returns The updated URLSearchParams object or the search params as a string.
 */
// @ts-expect-error // figure out how to type this
export const buildSearchParams: buildSearchParamsType = (
  key,
  value,
  searchParams,
  toSearchString,
) => {
  const nextSearchParams = new URLSearchParams(searchParams);

  if (isNullOrEmpty(value)) {
    nextSearchParams.delete(key);
  } else {
    nextSearchParams.set(key, String(value));
  }

  if (toSearchString) {
    return `?${nextSearchParams.toString()}`;
  }

  return nextSearchParams;
};

/**
 * Strip everything but host and pathname from a URL for display purposes —
 * no protocol, query string, or fragment. Values that aren't a valid
 * absolute URL are returned unchanged.
 *
 * @example
 * stripUrlProtocol('https://www.vestigas.com/de?ref=x#top') // 'www.vestigas.com/de'
 */
export const stripUrlProtocol = (url: string) => {
  try {
    const { host, pathname } = new URL(url);

    return `${host}${pathname === '/' ? '' : pathname}`;
  } catch {
    return url;
  }
};

/**
 * Parses `url` and returns it only if it's a well-formed `http:`/`https:`
 * URL — rejects `javascript:`, `data:`, other non-web schemes, and malformed
 * input. Use before rendering an untrusted URL (e.g. from a search agent) as
 * a link `href`.
 */
export const parseWebUrl = (url: string) => {
  try {
    const parsed = new URL(url);

    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};
